import { useState, useContext, useEffect, useCallback, useRef } from 'react' 
import { ReactSortable } from 'react-sortablejs'

import ActionsDropdown from '../../UI/ActionsDropdown'
import ResponseLoader from '../../UI/ResponseLoader'
import { DocumentsRow, ResizableDocumentColumn, DocumentsActions, ColumnsFilter } from '../'
import { GlobalContext, DocumentsContext, TeamContext, ColumnsContext } from '../../../context'
import { getTeamPaginationData, isCookieAccepted, saveTeamPaginationDataToLS, getTeamColumnsOrderData, saveTeamColumnsOrderDataToLS, removeTeamColumnsOrderData, saveTeamDocsColumnWidthsToLS, getTeamColumnsData } from '../../../utils'
import { perPageOptions } from '../../../constants'
import { useDocumentsActions } from '../../../hooks'

let droppableCounter = 0

const DocumentsView = ({ filteredDocs, onSort, selectedDocuments, onSetSelectedDocuments, allChecked, onAllCheckedChange, paginatedData, setPaginatedData, refreshDocs, ready, setAllChecked }) => {
  const { t } = useContext(GlobalContext)
  const { setDocsSelectedFilters, currentFilter } = useContext(DocumentsContext)
  const { selectedTeam } = useContext(TeamContext)
  const { docsTableWidth, setDocsTableWidth, docsTableColumnsWidths, setDocsTableColumnsWidths } = useContext(ColumnsContext)
  const { uploadDocumentsByDrop } = useDocumentsActions()
  const [defaultFields, setDefaultFields] = useState([])
  const [columns, setColumns] = useState([
    { label: t('dashboard.title'), value: 'name', icon: 'text_fields', checked: true },
    { label: t('dashboard.completeness'), value: 'progress', icon: 'percent', checked: true },
    { label: t('dashboard.template'), value: 'sort_template', icon: 'view_quilt', checked: true },
    { label: t('dashboard.status'), value: 'status_label', icon: 'circle', checked: true },
    { label: t('general.user'), value: 'user', icon: 'group', checked: true },
    { label: t('dashboard.files'), value: 'sort_attachments', icon: 'attachment', checked: true },
    { label: t('dashboard.tasks'), value: 'sort_tasks', icon: 'task_alt', checked: true },
    { label: t('dashboard.dates'), value: 'sort_dates', icon: 'calendar_month', checked: true },
    { label: t('dashboard.alerts'), value: 'sort_alerts', icon: 'alarm', checked: true },
    { label: t('dashboard.tags'), value: 'sort_tags', icon: 'tag', checked: true },
    { label: t('dashboard.created_on'), value: 'meta.created', icon: 'calendar_month', checked: true },
    { label: t('dashboard.updated_on'), value: 'meta.updated', icon: 'calendar_month', checked: true },
  ])
  const [search, setSearch] = useState('')
  const [perPage, setPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [allPages, setAllPages] = useState(1)
  const [paginationReady, setPaginationReady] = useState(false)
  const [columnsUpdated, setColumnsUpdated] = useState(false)
  const [activeResizableColumn, setActiveResizableColumn] = useState(null)
  const [tableWidthReady, setTableWidthReady] = useState(false)
  const [showDroppableHover, setShowDroppableHover] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const colRefs = useRef([])

  // Check in LS to see if pagination data was saved and if it is saved update pagination state variables
  useEffect(() => {
    const paginationData = getTeamPaginationData()
    if(selectedTeam && paginationData && paginationData[selectedTeam.id] && paginationData[selectedTeam.id].documents) {
      const documentsData = paginationData[selectedTeam.id].documents
      let all = Math.ceil(filteredDocs.length / documentsData.items || 10)
      documentsData.items && setPerPage(documentsData.items)
      documentsData.current && setCurrentPage(documentsData.current <= all ? documentsData.current : 1)
      documentsData.all && setAllPages(documentsData.all)
    }
  }, [filteredDocs, selectedTeam])

  // Add custom columns to columns array, order columns...
  useEffect(() => {
    if(selectedTeam && !columnsUpdated) {
      let shouldUpdate = false
      let newColumns = [...columns]

      // Add custom fields to the table
      if(selectedTeam.custom_fields) {
        shouldUpdate = true
        let customFields = []
        selectedTeam.custom_fields.forEach(f => {
          let val = `sort_cf_${f.id}`
          if(f.type === 'multiselect') {
            val = `sort_cf_${f.id}__arr`
          }
          customFields.push({ 
            label: f.label, 
            value: val, 
            icon: 'temp_preferences_custom',
            custom: true,
            customType: f.type, 
            customOptions: f.options || [],
            checked: false,
          })
        })
        newColumns.push(...customFields)
      }

      setDefaultFields([...newColumns, { label: '', value: 'helper', icon: '', checked: true }])

      // Show only checked columns
      const teamColData = getTeamColumnsData()
      if(teamColData && teamColData[selectedTeam.id]) {
        const docsColsValues = teamColData[selectedTeam.id].documents
        let arr = []
        if(docsColsValues) {
          arr = newColumns.map(c => docsColsValues.includes(c.value) ? {...c, checked: true} : {...c, checked: c.value !== 'name' ? false : true})
          newColumns = arr
        }
      }

      // Change order
      const colOrderValues = getTeamColumnsOrderData()
      if(colOrderValues && colOrderValues[selectedTeam.id]) {
        const docColOrderValues = colOrderValues[selectedTeam.id].documents 
        if(docColOrderValues) {
          let orderedCols = [] 
          docColOrderValues.forEach(val => {
            const col = newColumns.find(c => c.value === val)
            col && orderedCols.push(col)
          })
          // Check if there are some columns that were not saved to columns order array, if so add them 
          const filterHiddenValues = [] 
          newColumns.forEach(c => {
            if(!docColOrderValues.includes(c.value)) {
              filterHiddenValues.push(c)
            }
          })
          if(filterHiddenValues.length > 0) {
            orderedCols.push(...filterHiddenValues)
          }
          newColumns = orderedCols
          shouldUpdate = true
        }
      }

      // Add helper column
      newColumns.push({ label: '', value: 'helper', icon: '', checked: true })
      shouldUpdate = true

      // Update only if new columns are added or order changed
      if(shouldUpdate) {
        setColumns(newColumns)
      }
      setColumnsUpdated(true)
    }
  }, [selectedTeam, columnsUpdated, columns])

  // Set pagination ready to false on currentFilter change
  useEffect(() => {
    setPaginationReady(false)
  }, [currentFilter])

  // Calculate table width
  const calculateTableWidth = useCallback(() => {
    let width = 0
    let columnsSaved = false
    let savedColumns = []
    const carbonColumns = getTeamColumnsData()
    if(carbonColumns && carbonColumns[selectedTeam?.id]) {
      const cols = carbonColumns[selectedTeam?.id].documents
      if(cols) {
        columnsSaved = true
        savedColumns = cols
      }
    }
    columns.forEach(col => {
      if(col.value !== 'helper') {
        if(columnsSaved) {
          if(savedColumns.includes(col.value)) {
            width += docsTableColumnsWidths[col.value] || 200
          }
        }else {
          if(col.checked) {
            width += docsTableColumnsWidths[col.value] || 200
          }
        }
      }
    })
    setDocsTableWidth(width)
  }, [columns, docsTableColumnsWidths, selectedTeam, setDocsTableWidth])

  // Set width to each column and calculate table width
  useEffect(() => {
    if(!tableWidthReady) {
      calculateTableWidth()
      setTableWidthReady(true)
    }
  }, [tableWidthReady, calculateTableWidth])

  // Change paginated data arr when filtered arr changes
  useEffect(() => {
    let current = currentPage
    let sliceFrom = current === 1 ? 0 : (current - 1) * perPage
    let slicedData
    if(sliceFrom < filteredDocs.length) {
      slicedData = filteredDocs.slice(sliceFrom, sliceFrom + perPage)
    }else {
      slicedData = filteredDocs.slice(0, perPage)
    }
    setPaginatedData(slicedData)
    let all = Math.ceil(filteredDocs.length / perPage)
    setAllPages(all === 0 ? 1 : all)
    setTimeout(() => setPaginationReady(true), 200)
  }, [filteredDocs, perPage, currentPage, allPages, setPaginatedData])

  // On search change
  const handleSearchChange = (e) => {
    setSearch(e.target.value)
    setDocsSelectedFilters(prev => ({...prev, search: e.target.value}))
    onSetSelectedDocuments([])
  }

  // On prev button click 
  const handlePrevButtonClick = (e) => {
    e.preventDefault()
    if(currentPage === 1) return 
    setCurrentPage(currentPage - 1)
    onSetSelectedDocuments([])
    setAllChecked(false)
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, perPage, currentPage - 1, allPages, 'documents')
    }
  }

  // On next button click 
  const handleNextButtonClick = (e) => {
    e.preventDefault()
    if(currentPage === allPages) return 
    setCurrentPage(currentPage + 1)
    onSetSelectedDocuments([])
    setAllChecked(false)
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, perPage, currentPage + 1, allPages, 'documents')
    }
  }

  // On go to specific page
  const handleGoToPage = (e, page) => {
    e.preventDefault()
    if(currentPage === page) return 
    setCurrentPage(page)
    onSetSelectedDocuments([])
    setAllChecked(false)
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, perPage, page, allPages, 'documents')
    }
  }

  // Per page change
  const handlePerPageChange = (e, num) => {
    e.preventDefault()
    setPerPage(num)
    onSetSelectedDocuments([])
    setAllChecked(false)
    if(Math.ceil(filteredDocs.length / num) < currentPage) {
      setCurrentPage(1)
    }
    window.document.body.click()
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, num, currentPage, allPages, 'documents')
    }
  }

  // Save columns order
  const handleSaveColumnsOrder = useCallback(() => {
    const colOrderValues = getTeamColumnsOrderData()
    if(isCookieAccepted()) {
      let arr = columns.map(f => f.value)
      saveTeamColumnsOrderDataToLS(selectedTeam?.id, arr, 'documents')
    }else {
      if(colOrderValues) {
        removeTeamColumnsOrderData()
      } 
    }
  }, [columns, selectedTeam])

  // On mouse down - set active resizable column
  const handleMouseDown = useCallback((idx, val) => {
    setActiveResizableColumn({ index: idx, value: val })
  }, [])

  // On mouse move - only when there is active resizable column
  const handleMouseMove = useCallback((e) => {
    if(selectedTeam && activeResizableColumn !== null) {
      const el = colRefs.current[activeResizableColumn.index]
      if(!el) return
      const rect = el.getBoundingClientRect()
      const width = e.clientX - rect.x

      if(width > 200) {
        el.style.width = width + 'px'
        let newWidths
        newWidths = {...docsTableColumnsWidths}
        newWidths[activeResizableColumn.value] = width
        setDocsTableColumnsWidths(newWidths)

        let totalWidth = 0
        columns.forEach(col => {
          if(col.value !== 'helper') {
            totalWidth += newWidths[col.value] || 200
          }
        })
        setDocsTableWidth(totalWidth)
        if(isCookieAccepted()) {
          saveTeamDocsColumnWidthsToLS(selectedTeam.id, newWidths)
        }
      }
    }
    // eslint-disable-next-line
  }, [activeResizableColumn, columns, selectedTeam])

  // Add mouse event listeners
  useEffect(() => {
    const handleMouseUp = () => {
      setActiveResizableColumn(null)
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }

    if (activeResizableColumn !== null) {
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleMouseUp)
    }
  }, [activeResizableColumn, handleMouseMove])

  // On file drag enter
  const handleFilesDragEnter = useCallback((e) => {
    e.preventDefault()
    if(!e.dataTransfer.types.includes('Files')) return
    droppableCounter++
    if(!showDroppableHover) {
      setShowDroppableHover(true)
    }
  }, [showDroppableHover])

  // On file drag leave
  const handleFilesDragLeave = useCallback((e) => {
    e.preventDefault()
    if(!e.dataTransfer.types.includes('Files')) return
    droppableCounter--
    if(showDroppableHover && droppableCounter === 0) {
      setShowDroppableHover(false)
    }
  }, [showDroppableHover])

  // On file drag over
  const handleFilesDragOver = useCallback((e) => {
    e.preventDefault()
  }, [])

  // On files drop - create document if file is dropped into table(list view)
  const handleFilesDrop = useCallback(async (e) => {
    e.preventDefault()
    const uploaded = await uploadDocumentsByDrop({ e, setShowDroppableHover, droppableCounter, setUploading, setProgress })
    if(uploaded) {
      refreshDocs()
    }
  }, [uploadDocumentsByDrop, refreshDocs])

  return (
    <div 
      className="list-view-v2"
      onDrop={handleFilesDrop}
      onDragOver={handleFilesDragOver}
      onDragEnter={handleFilesDragEnter}
      onDragLeave={handleFilesDragLeave}
    >
      {showDroppableHover && (
        <div className="list-view-droppable-area">
          <div className="list-view-droppable-area__inner">
            <div className="icon">
              <span className="material-symbols-outlined">cloud_upload</span>
            </div>
            <p>{t('general.droppable_hover_text')}</p>
          </div>
        </div>
      )}
      <div className="list-view-v2__head">
        <div className="search">
          <div className="icon">
            <span className="material-symbols-outlined weight-300">search</span>
          </div>
          <input type="text" value={search} onChange={handleSearchChange} />
        </div>
        <div className="per-page">
          <ActionsDropdown
            trigger={<a href="#/" className="text-style-4">{perPage} {t('general.per_page')} <span className="material-symbols-outlined">expand_more</span></a>}
            hideHeader
            dropdownClass="pagination-dropdown"
            width={120}
          >
            <ul>
              {perPageOptions.map((num, i) => (
                <li key={i}>
                  <div className="link">
                    <a href="/#" onClick={(e) => handlePerPageChange(e, num)}>{num}</a>
                  </div>
                </li>
              ))}
            </ul>
          </ActionsDropdown>
        </div>
        <div className="filter">
          <ColumnsFilter 
            columns={columns} 
            setColumns={setColumns} 
            onOrderSave={handleSaveColumnsOrder} 
            view="documents" 
            defaultFields={defaultFields}
            calculateTableWidth={calculateTableWidth}
          />
        </div>
      </div>
      
      <div className="list-view-v2__body">
        <div className="list-view-table-wrapper u-custom-scrollbar u-custom-scrollbar--3">
          {selectedDocuments.length > 0 && (
            <DocumentsActions allChecked={allChecked} onAllCheckedChange={onAllCheckedChange} selectedDocuments={selectedDocuments} setSelectedDocuments={onSetSelectedDocuments} refreshDocs={refreshDocs} />
          )}
          <div className="list-view-table" style={{minWidth: docsTableWidth}}>
            <div className="list-view-table__head">
              <ReactSortable 
                list={columns} 
                setList={setColumns} 
                animation={200} 
                delayOnTouchStart={true} 
                delay={2} 
                dragClass="dragged-field--2"
                ghostClass="selected-field"
                filter=".filtered-out"
                draggable=".draggable-item"
                onEnd={handleSaveColumnsOrder}
                handle=".sort"      
                className="sortable-row-items"  
              >
                {columns.filter(c => c.checked).map((column, idx) => {
                  return (
                    <ResizableDocumentColumn column={column} idx={idx} onMouseDown={handleMouseDown} onSort={onSort} ref={(item) => colRefs.current[idx] = item} key={idx} />
                  )
                })}
              </ReactSortable>
              <div className="list-view-table__cell checkbox">
                {!allChecked 
                  ? <span className="material-symbols-outlined" onClick={onAllCheckedChange}>check_box_outline_blank</span>
                  : <span className="material-symbols-outlined filled" onClick={onAllCheckedChange}>check_box</span>
                }
              </div>
            </div>
            <div className="list-view-table__body">
              {(!ready || !paginationReady) && (
                <div className="loader-wrapper"><div className="loader-v2"></div></div>
              )}
              {ready && paginationReady && paginatedData.map((doc, idx) => {

                return (
                  <DocumentsRow 
                    key={idx} 
                    doc={doc}
                    id={doc.id}
                    selectedDocuments={selectedDocuments}
                    onSetSelectedDocuments={onSetSelectedDocuments}
                    allChecked={allChecked}
                    columns={columns}
                    refreshDocs={refreshDocs}
                  />
                )
              })}
              {ready && paginationReady && paginatedData.length === 0 && (
                <div className="no-data">
                  <p className="text-style-3">{t('folder.empty')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="list-view-v2__foot">
        <div className="pagination-v2">
          <a href="/#" className="prev" onClick={handlePrevButtonClick}><span className="material-symbols-outlined weight-300">arrow_back</span></a>
          {Array(allPages).fill().map((_, idx) => {
            return (
              <a href="/#" key={idx} className={`${idx+1 === currentPage ? 'active' : ''}`} onClick={(e) => handleGoToPage(e, idx+1)}>
                {idx+1}
              </a>
            )
          })}
          <a href="/#" className="next" onClick={handleNextButtonClick}><span className="material-symbols-outlined weight-300">arrow_forward</span></a>
        </div>
      </div>

      {uploading && <ResponseLoader progress={progress} />}
    </div>
  )
}

export default DocumentsView